/**
 * Relatórios > Financeiro > DRE.
 *
 * DRE gerencial mensal/anual com análise vertical, comparativos,
 * indicadores (margens, ponto de equilíbrio, cobertura, mix) e série mensal.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  Calculator,
  Calendar,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Info,
  Search,
} from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { clsx } from 'clsx';
import financialReportsService from '@services/financialReportsService';
import financialService from '@services/financialService';
import type {
  DreBreakdown,
  DreInvoicesBreakdown,
  DreReport,
  DreRevenueStream,
  ExpenseTransaction,
} from '@/types/financialReports';
import type { DreGroup } from '@/types/financial';
import { formatCurrencyBRL, formatDateBR, formatDateTimeBR } from '@pages/financial/financialUtils';
import {
  ErrorBox,
  LoadingSpinner,
  ModalShell,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
  selectCls,
} from '@pages/financial/financialShared';
import {
  BasisToggle,
  ChartTooltip,
  CompanySelect,
  FutureCompetenceNotice,
  ProjectedBanner,
  SourceBadge,
  getMonthOptions,
  getYearOptions,
  kFormatter,
  useCompanies,
} from './financialReportsShared';
import { useUserPreferences, PREF_FIN_REPORT_DRE } from '@hooks/useUserPreferences';
import { useIsMobile } from '@hooks/useIsMobile';
import {
  buildDreRows,
  currentCompetence,
  dreSubtotalFormula,
  formatPct,
  isFutureCompetence,
  monthLabelPtBR,
} from './financialReportsUtils';
import type { ReportBasis } from '@/types/financialReports';
import type { DreDisplayRow, DreSubtotalFormula } from './financialReportsUtils';
import { DreConceptsContent } from './DreConceptsContent';

type DreMode = 'mensal' | 'anual';
type DreSubTab = 'demonstrativo' | 'conceitos';

interface DrePrefs extends Record<string, unknown> {
  mode?: DreMode;
  company_id?: number;
}

/** Grupos DRE de receita cuja memória de cálculo vem de notas fiscais. */
const revenueStreamFromCode = (code: string): DreRevenueStream | null =>
  code === 'RECEITA_PRODUTO' ? 'product' : code === 'RECEITA_SERVICO' ? 'service' : null;

const MONTHS_FULL_PTBR = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** "Junho/2026" (mensal) ou "Ano 2026" (anual). */
const periodLabel = (year: number, month: number | null): string =>
  month != null ? `${MONTHS_FULL_PTBR[month - 1]}/${year}` : `Ano ${year}`;

/** Nota fixa da memória de cálculo (origem dos números). */
const BREAKDOWN_NOTE =
  'Regime de competência: conta corrente (excluindo pagamentos de fatura de cartão) + lançamentos de ' +
  'cartão pela competência da fatura. Classificação via de-para Grupo DRE (herança da categoria pai ' +
  'quando a subcategoria não tem mapeamento próprio).';

// ─── Card de indicador com tooltip explicativo ──────────────────

const IndicatorCard: React.FC<{ label: string; value: string; hint: string }> = ({ label, value, hint }) => {
  // Hover não existe no toque: o card também abre/fecha a explicação ao ser
  // tocado (e a fecha ao tocar de novo).
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative group bg-white border border-slate-200 rounded-xl shadow-sm p-4 cursor-help"
      onClick={() => setOpen(o => !o)}
    >
      <p className="text-sm text-slate-500 flex items-center gap-1">
        {label}
        <Info size={13} className="text-slate-400 shrink-0" />
      </p>
      <p className="text-lg sm:text-xl font-bold mt-1 text-slate-900 break-words">{value}</p>
      <div
        className={clsx(
          'pointer-events-none absolute left-0 top-full z-10 mt-1 w-64 max-w-[calc(100vw-3rem)] rounded-lg bg-slate-800 p-2.5 text-xs leading-relaxed text-white shadow-lg group-hover:block',
          open ? 'block' : 'hidden',
        )}
      >
        {hint}
      </div>
    </div>
  );
};

// ─── Célula de valor monetário ──────────────────────────────────

const MoneyCell: React.FC<{ value: number; bold?: boolean }> = ({ value, bold }) => (
  <td
    className={clsx(
      'py-2 px-3 text-right whitespace-nowrap',
      bold ? 'font-semibold' : '',
      value < 0 ? 'text-red-600' : bold ? 'text-slate-900' : 'text-slate-700',
    )}
  >
    {formatCurrencyBRL(value)}
  </td>
);

const DreTableRow: React.FC<{ row: DreDisplayRow; onClick?: () => void }> = ({ row, onClick }) => {
  if (row.kind === 'memoHeader') {
    return (
      <tr className="bg-slate-100">
        <td colSpan={6} className="py-2 px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {row.name}
        </td>
      </tr>
    );
  }
  const isSubtotal = row.kind === 'subtotal';
  return (
    <tr
      onClick={onClick}
      title={onClick ? 'Ver memória de cálculo' : undefined}
      className={clsx(
        'group border-b border-slate-100',
        isSubtotal ? 'bg-slate-50' : 'hover:bg-slate-50',
        onClick && (isSubtotal ? 'cursor-pointer hover:bg-slate-100' : 'cursor-pointer'),
      )}
    >
      <td className={clsx('py-2 px-3', isSubtotal ? 'font-semibold text-slate-900' : 'pl-8 text-slate-700')}>
        <span className="inline-flex items-center gap-1.5">
          {row.name}
          {onClick && (
            <Search size={13} className="shrink-0 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </span>
      </td>
      <MoneyCell value={row.current} bold={isSubtotal} />
      <td className="py-2 px-3 text-right text-slate-500">
        {row.verticalPct === null ? '—' : formatPct(row.verticalPct)}
      </td>
      <MoneyCell value={row.prevPeriod} bold={isSubtotal} />
      <MoneyCell value={row.samePeriodLastYear} bold={isSubtotal} />
      <MoneyCell value={row.ytd} bold={isSubtotal} />
    </tr>
  );
};

// ─── Modal "Memória de Cálculo" — grupo (breakdown da API) ──────

/** Subtítulo padrão das modais de memória de cálculo (período + empresa). */
const MemoSubtitle: React.FC<{ period: string; company: string }> = ({ period, company }) => (
  <p className="text-sm text-slate-500">
    {period} · {company}
  </p>
);

const InheritedBadge: React.FC = () => (
  <span className="ml-2 inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-medium text-slate-500 align-middle">
    herdado
  </span>
);

/** Célula de valor com sinal REAL (vermelho quando negativo). */
const SignedMoneyCell: React.FC<{ value: number; muted?: boolean }> = ({ value, muted }) => (
  <td
    className={clsx(
      'py-2 px-3 text-right whitespace-nowrap',
      value < 0 ? 'text-red-600' : muted ? 'text-slate-500' : 'text-slate-700',
    )}
  >
    {formatCurrencyBRL(value)}
  </td>
);

/** Nota de origem dos dados das receitas (memória por notas fiscais). */
const invoiceOriginNote = (stream: DreRevenueStream): string =>
  stream === 'product'
    ? 'Origem dos dados: notas fiscais de PRODUTO emitidas ([Financial].[Invoice], notas de saída não ' +
      'canceladas, pela data de emissão).'
    : 'Origem dos dados: notas fiscais de SERVIÇO cadastradas em Vendas → Notas Fiscais de Serviço, ' +
      'pela data de emissão.';

const DreBreakdownModal: React.FC<{
  year: number;
  month: number | null;
  companyId: number | null;
  companyLabel: string;
  dreGroupId: number | null;
  groupCode: string;
  groupName: string;
  basis: ReportBasis;
  onClose: () => void;
}> = ({ year, month, companyId, companyLabel, dreGroupId, groupCode, groupName, basis, onClose }) => {
  // Grupos de receita: memória = notas fiscais emitidas (não usa breakdown).
  const stream = revenueStreamFromCode(groupCode);

  const [breakdown, setBreakdown] = useState<DreBreakdown | null>(null);
  const [invoices, setInvoices] = useState<DreInvoicesBreakdown | null>(null);
  const [loading, setLoading] = useState(stream != null || dreGroupId != null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Painel de lançamentos (segunda seção da modal)
  const [txTarget, setTxTarget] = useState<{ erpId: number; title: string } | null>(null);
  const [txRows, setTxRows] = useState<ExpenseTransaction[] | null>(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);

  const fetchBreakdown = useCallback(async () => {
    if (stream == null && dreGroupId == null) return;
    setLoading(true);
    setError(null);
    try {
      if (stream != null) {
        setInvoices(await financialReportsService.getDreInvoices(year, stream, month, companyId));
      } else {
        setBreakdown(await financialReportsService.getDreBreakdown(year, dreGroupId!, month, companyId, basis));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar memória de cálculo');
      setBreakdown(null);
      setInvoices(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, companyId, dreGroupId, stream, basis]);

  useEffect(() => {
    fetchBreakdown();
  }, [fetchBreakdown]);

  const openTransactions = useCallback(
    async (erpId: number, title: string) => {
      setTxTarget({ erpId, title });
      setTxRows(null);
      setTxLoading(true);
      setTxError(null);
      try {
        setTxRows(await financialReportsService.getExpenseTransactions(year, month, erpId, companyId));
      } catch (err) {
        setTxError(err instanceof Error ? err.message : 'Erro ao carregar lançamentos');
      } finally {
        setTxLoading(false);
      }
    },
    [year, month, companyId],
  );

  const backToSummary = () => {
    setTxTarget(null);
    setTxRows(null);
    setTxError(null);
  };

  const toggleCategory = (erpId: number) =>
    setExpanded(s => {
      const next = new Set(s);
      if (next.has(erpId)) next.delete(erpId);
      else next.add(erpId);
      return next;
    });

  const txTotal = (txRows ?? []).reduce((s, t) => s + t.amount, 0);

  return (
    <ModalShell
      title={`Memória de Cálculo — ${groupName}`}
      icon={<Calculator size={18} className="text-emerald-600" />}
      onClose={onClose}
      maxWidth="max-w-4xl"
      footer={
        <button type="button" className={secondaryBtnCls} onClick={onClose}>
          Fechar
        </button>
      }
    >
      <MemoSubtitle period={periodLabel(year, month)} company={companyLabel} />

      {stream == null && dreGroupId == null && (
        <ErrorBox message={`Não foi possível identificar o grupo DRE desta linha (${groupName}).`} />
      )}
      {loading && <LoadingSpinner />}
      {error && <ErrorBox message={error} onRetry={fetchBreakdown} />}

      {/* Receitas: memória = notas fiscais emitidas no período */}
      {!loading && !error && stream != null && invoices && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="text-2xl font-bold text-slate-900">{formatCurrencyBRL(invoices.total)}</p>
            <p className="text-sm text-slate-500">{invoices.count} notas fiscais</p>
          </div>
          <p className="text-xs leading-relaxed text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
            {invoiceOriginNote(stream)}
          </p>

          {invoices.invoices.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Nenhuma nota fiscal encontrada no período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Nº</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Data</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Cliente / Descrição</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono text-xs text-slate-700 whitespace-nowrap">
                        {stream === 'product' && inv.serie ? `${inv.number}/${inv.serie}` : inv.number}
                      </td>
                      <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{formatDateTimeBR(inv.date)}</td>
                      <td className="py-2 px-3 text-slate-700">
                        {inv.customerName ?? inv.description ?? '—'}
                        {inv.customerName && inv.description && (
                          <span className="block text-xs text-slate-400">{inv.description}</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-slate-900 whitespace-nowrap">
                        {formatCurrencyBRL(inv.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-semibold">
                    <td colSpan={3} className="py-2 px-3 text-slate-700">Total ({invoices.count} notas)</td>
                    <td className="py-2 px-3 text-right text-slate-900">{formatCurrencyBRL(invoices.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}

      {!loading && !error && stream == null && breakdown && (
        txTarget === null ? (
          <>
            {/* Cabeçalho: total + nº de lançamentos + nota de origem */}
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <p className={clsx('text-2xl font-bold', breakdown.total < 0 ? 'text-red-600' : 'text-slate-900')}>
                {formatCurrencyBRL(breakdown.total)}
              </p>
              <p className="text-sm text-slate-500">{breakdown.transactionCount} lançamentos</p>
            </div>
            <p className="text-xs leading-relaxed text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
              {BREAKDOWN_NOTE}
            </p>

            {/* Categoria → Subcategoria */}
            {breakdown.categories.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nenhum lançamento encontrado no período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Lançamentos</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.categories.map(cat => {
                      const catExpanded = expanded.has(cat.erpId);
                      const hasSubs = cat.subcategories.length > 0;
                      return (
                        <React.Fragment key={cat.erpId}>
                          <tr className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-3">
                              <span className="inline-flex items-center gap-1">
                                {hasSubs ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleCategory(cat.erpId)}
                                    className="text-slate-400 hover:text-slate-600"
                                    title={catExpanded ? 'Recolher subcategorias' : 'Expandir subcategorias'}
                                  >
                                    {catExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </button>
                                ) : (
                                  <span className="w-[14px]" />
                                )}
                                <button
                                  type="button"
                                  onClick={() => openTransactions(cat.erpId, cat.description)}
                                  className="text-slate-700 hover:text-emerald-700 hover:underline text-left"
                                  title="Ver lançamentos"
                                >
                                  {cat.description}
                                </button>
                                {cat.inheritedFromParent && <InheritedBadge />}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right text-slate-500">{cat.transactionCount}</td>
                            <SignedMoneyCell value={cat.total} />
                          </tr>
                          {catExpanded &&
                            cat.subcategories.map(sub => (
                              <tr key={sub.erpId} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-1.5 px-3 pl-4 sm:pl-10">
                                  <button
                                    type="button"
                                    onClick={() => openTransactions(sub.erpId, sub.description)}
                                    className="text-slate-500 hover:text-emerald-700 hover:underline text-left"
                                    title="Ver lançamentos"
                                  >
                                    {sub.description}
                                  </button>
                                  {sub.inheritedFromParent && <InheritedBadge />}
                                </td>
                                <td className="py-1.5 px-3 text-right text-slate-400">{sub.transactionCount}</td>
                                <SignedMoneyCell value={sub.total} muted />
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold">
                      <td className="py-2 px-3 text-slate-700">Total</td>
                      <td className="py-2 px-3 text-right text-slate-700">{breakdown.transactionCount}</td>
                      <td className={clsx('py-2 px-3 text-right', breakdown.total < 0 ? 'text-red-600' : 'text-slate-900')}>
                        {formatCurrencyBRL(breakdown.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Painel de lançamentos da categoria/subcategoria */}
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={backToSummary}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline"
              >
                <ArrowLeft size={14} /> Voltar ao resumo
              </button>
              <p className="text-sm font-semibold text-slate-800 truncate">Lançamentos — {txTarget.title}</p>
            </div>

            {txLoading && <LoadingSpinner />}
            {txError && <ErrorBox message={txError} onRetry={() => openTransactions(txTarget.erpId, txTarget.title)} />}
            {!txLoading && !txError && txRows && (
              txRows.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">Nenhum lançamento encontrado no período.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Fonte</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Data</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Conta / Cartão</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Mês Fatura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txRows.map(t => (
                        // Um pai rateado vem em N linhas com o mesmo transactionId
                        // (uma por categoria) — a parte é o que identifica a linha.
                        <tr key={`${t.source}-${t.allocationId ?? t.transactionId}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3"><SourceBadge source={t.source} /></td>
                          <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{formatDateBR(t.date)}</td>
                          <td className="py-2 px-3 text-slate-700">
                            {t.description}
                            {t.allocationOf != null && (
                              <span className="block text-xs text-slate-500">
                                parte de {formatCurrencyBRL(Math.abs(t.allocationOf))} · rateado
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-slate-900 whitespace-nowrap">
                            {formatCurrencyBRL(Math.abs(t.amount))}
                          </td>
                          <td className="py-2 px-3 text-slate-700">{t.accountOrCardName}</td>
                          <td className="py-2 px-3 text-slate-500">{t.invoiceMonth ? monthLabelPtBR(t.invoiceMonth) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 font-semibold">
                        <td colSpan={3} className="py-2 px-3 text-slate-700">Total ({txRows.length} lançamentos)</td>
                        <td className="py-2 px-3 text-right text-slate-900">{formatCurrencyBRL(Math.abs(txTotal))}</td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )
            )}
          </>
        )
      )}
    </ModalShell>
  );
};

// ─── Modal "Memória de Cálculo" — subtotal (fórmula client-side) ─

const DreFormulaModal: React.FC<{
  formula: DreSubtotalFormula;
  period: string;
  companyLabel: string;
  onClose: () => void;
}> = ({ formula, period, companyLabel, onClose }) => (
  <ModalShell
    title={`Memória de Cálculo — ${formula.label}`}
    icon={<Calculator size={18} className="text-emerald-600" />}
    onClose={onClose}
    maxWidth="max-w-lg"
    footer={
      <button type="button" className={secondaryBtnCls} onClick={onClose}>
        Fechar
      </button>
    }
  >
    <MemoSubtitle period={period} company={companyLabel} />
    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
      {formula.parts.length === 0 && (
        <p className="px-4 py-3 text-sm text-slate-500">Nenhum componente com valor no período.</p>
      )}
      {formula.parts.map((part, idx) => (
        <div key={`${part.label}-${idx}`} className="flex items-center justify-between gap-4 px-4 py-2 text-sm">
          <span className="text-slate-700">{part.label}</span>
          <span className="text-slate-700 whitespace-nowrap">
            {idx === 0 && part.op === '+' ? '' : part.op === '-' ? '− ' : '+ '}
            {formatCurrencyBRL(part.value)}
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm font-bold bg-slate-50">
        <span className="text-slate-900">= {formula.label}</span>
        <span className={clsx('whitespace-nowrap', formula.result < 0 ? 'text-red-600' : 'text-slate-900')}>
          {formatCurrencyBRL(formula.result)}
        </span>
      </div>
    </div>
  </ModalShell>
);

// ─── View principal ─────────────────────────────────────────────

export const DreReportView: React.FC = () => {
  const isMobile = useIsMobile();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [mode, setMode] = useState<DreMode>('mensal');
  const [subTab, setSubTab] = useState<DreSubTab>('demonstrativo');
  const [companyId, setCompanyId] = useState<number | null>(null);
  /**
   * Realizado × Projetado. Fora das preferências salvas de propósito: reabrir
   * a DRE dias depois já em "projetado", sem perceber, seria pior do que ter
   * de clicar de novo.
   */
  const [basis, setBasis] = useState<ReportBasis>('realizado');
  const { companies, companiesError } = useCompanies();

  const [report, setReport] = useState<DreReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Grupos DRE (id ← code) para a memória de cálculo — carregados uma vez.
  const [dreGroups, setDreGroups] = useState<DreGroup[]>([]);
  useEffect(() => {
    financialService
      .getDreGroups()
      .then(setDreGroups)
      .catch(() => setDreGroups([])); // falha silenciosa — a modal informa se o grupo não for resolvido
  }, []);
  const groupIdByCode = useMemo(
    () => new Map(dreGroups.map(g => [g.code, g.dreGroupId])),
    [dreGroups],
  );

  // Modais de memória de cálculo
  const [breakdownTarget, setBreakdownTarget] = useState<{
    dreGroupId: number | null;
    code: string;
    name: string;
  } | null>(null);
  const [formulaTarget, setFormulaTarget] = useState<DreSubtotalFormula | null>(null);

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<DrePrefs>(PREF_FIN_REPORT_DRE);
  const [filtersReady, setFiltersReady] = useState(false);

  // Restaura os filtros salvos antes da primeira busca (o período fica no mês corrente)
  useEffect(() => {
    if (!prefsLoaded) return;
    if (prefs?.mode === 'mensal' || prefs?.mode === 'anual') setMode(prefs.mode);
    if (prefs?.company_id != null) setCompanyId(prefs.company_id);
    setFiltersReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  useEffect(() => {
    if (!filtersReady) return;
    savePrefs({ mode, company_id: companyId ?? undefined });
  }, [filtersReady, mode, companyId, savePrefs]);

  const fetchData = useCallback(async () => {
    if (!filtersReady) return;
    setLoading(true);
    setError(null);
    try {
      setReport(
        await financialReportsService.getDreReport(year, mode === 'mensal' ? month : null, companyId, basis),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar DRE');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, mode, companyId, filtersReady, basis]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = report ? buildDreRows(report.lines, report.subtotals) : [];
  const chartData = (report?.monthlySeries ?? []).map(p => ({
    month: monthLabelPtBR(p.month),
    'Receita Líquida': p.receitaLiquida,
    'EBITDA': p.ebitda,
    'Resultado Líquido': p.resultadoLiquido,
  }));

  const prevPeriodLabel = mode === 'mensal' ? 'Mês Anterior' : 'Ano Anterior';
  const ind = report?.indicators;
  const competenceRef = currentCompetence();
  const futureInRealized =
    basis === 'realizado' &&
    isFutureCompetence(year, mode === 'mensal' ? month : 1, competenceRef);

  const monthOrNull = mode === 'mensal' ? month : null;
  const companyLabel =
    companyId == null
      ? 'Consolidado'
      : companies.find(c => c.company_id === companyId)?.name ?? `Empresa ${companyId}`;

  /** Handler de clique por linha: grupo → breakdown; subtotal → fórmula. */
  const rowClickHandler = (row: DreDisplayRow): (() => void) | undefined => {
    if (!report) return undefined;
    if (row.kind === 'line') {
      return () =>
        setBreakdownTarget({ dreGroupId: groupIdByCode.get(row.code) ?? null, code: row.code, name: row.name });
    }
    if (row.kind === 'subtotal') {
      const formula = dreSubtotalFormula(row.code, report.lines, report.subtotals);
      if (!formula) return undefined; // subtotal sem fórmula conhecida — não clicável
      return () => setFormulaTarget(formula);
    }
    return undefined;
  };

  return (
    <div className="space-y-6">
      {/* Sub-abas Demonstrativo | Conceitos */}
      <div className="border-b border-slate-200">
        <nav className="flex -mb-px gap-0 overflow-x-auto" aria-label="Sub-abas da DRE">
          {(
            [
              { id: 'demonstrativo', label: 'Demonstrativo', icon: <FileSpreadsheet size={14} /> },
              { id: 'conceitos', label: 'Conceitos', icon: <BookOpen size={14} /> },
            ] as { id: DreSubTab; label: string; icon: React.ReactNode }[]
          ).map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSubTab(tab.id)}
              className={clsx(
                'inline-flex items-center gap-1.5 whitespace-nowrap py-2 px-4 text-sm font-medium border-b-2 transition-colors',
                subTab === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {subTab === 'conceitos' && <DreConceptsContent />}

      {subTab === 'demonstrativo' && (
        <>
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Período</label>
            <div className="inline-flex w-full sm:w-auto rounded-lg border border-slate-300 overflow-hidden">
              {(['mensal', 'anual'] as DreMode[]).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={clsx(
                    'flex-1 sm:flex-none px-4 py-2 text-sm font-medium transition-colors',
                    mode === m ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {m === 'mensal' ? 'Mensal' : 'Anual'}
                </button>
              ))}
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Base</label>
            <BasisToggle value={basis} onChange={setBasis} />
          </div>
          {mode === 'mensal' && (
            <div className="w-full sm:w-auto">
              <label className={labelCls}>Mês</label>
              <select className={`${selectCls} w-full sm:w-32`} value={month} onChange={e => setMonth(Number(e.target.value))}>
                {getMonthOptions({ year }).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Ano</label>
            <select className={`${selectCls} w-full sm:w-28`} value={year} onChange={e => setYear(Number(e.target.value))}>
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Empresa</label>
            <CompanySelect companies={companies} value={companyId} onChange={setCompanyId} />
          </div>
          <button onClick={fetchData} disabled={loading} className={`${primaryBtnCls} w-full sm:w-auto justify-center`}>
            <Calendar size={16} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {companiesError && <ErrorBox message={companiesError} />}
      {basis === 'projetado' && <ProjectedBanner />}
      {error && <ErrorBox message={error} onRetry={fetchData} />}
      {loading && <LoadingSpinner />}

      {!loading && !error && report && (
        <>
          {/* Indicadores — ausentes no projetado: sem receita não há margem. */}
          {!ind ? (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-sm font-medium text-slate-700">Indicadores não calculados</p>
              <p className="text-xs text-slate-500 mt-1">
                A receita da DRE vem de notas fiscais emitidas; para uma competência futura ela não
                existe. Sem receita, margens, ponto de equilíbrio e mix não teriam base de cálculo —
                então não são exibidos em vez de aparecerem errados. Os valores por grupo abaixo
                continuam válidos.
              </p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <IndicatorCard
              label="Margem de Contribuição"
              value={formatPct(ind.margemContribuicaoPct)}
              hint="Quanto sobra de cada venda, após custos e despesas variáveis, para pagar a estrutura fixa."
            />
            <IndicatorCard
              label="Margem EBITDA"
              value={formatPct(ind.margemEbitdaPct)}
              hint="Geração de caixa operacional antes de juros, impostos e depreciação, em % da receita líquida."
            />
            <IndicatorCard
              label="Margem Líquida"
              value={formatPct(ind.margemLiquidaPct)}
              hint="Percentual da receita líquida que vira resultado final após todas as despesas."
            />
            <IndicatorCard
              label="Ponto de Equilíbrio"
              value={formatCurrencyBRL(ind.pontoEquilibrio)}
              hint="Faturamento mínimo mensal necessário para não ter prejuízo."
            />
            <IndicatorCard
              label="Cobertura Custo Fixo"
              value={`${ind.coberturaCustoFixo.toFixed(1).replace('.', ',')}×`}
              hint="Quantas vezes a margem de contribuição cobre a estrutura fixa. Acima de 1× a operação é superavitária."
            />
            <IndicatorCard
              label="Mix Produto × Serviço"
              value={`${formatPct(ind.mixProdutoPct, 0)} × ${formatPct(ind.mixServicoPct, 0)}`}
              hint="Participação de produtos × serviços na composição da receita."
            />
          </div>
          )}

          {/* Tabela DRE */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-base font-semibold text-slate-800 mb-3">
              DRE Gerencial — {mode === 'mensal' ? `${getMonthOptions()[month - 1].label}/${year}` : year}
            </h3>
            {rows.length === 0 ? (
              futureInRealized ? (
                <FutureCompetenceNotice
                  year={year}
                  month={mode === 'mensal' ? month : null}
                  onSwitchToProjected={() => setBasis('projetado')}
                />
              ) : (
                <p className="text-sm text-slate-500 py-6 text-center">
                  Nenhum lançamento encontrado no período.
                </p>
              )
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Conta</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Atual</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">AV %</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">{prevPeriodLabel}</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Mesmo Período Ano Ant.</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Acumulado (YTD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <DreTableRow key={`${row.code}-${idx}`} row={row} onClick={rowClickHandler(row)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Série mensal */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <BarChart3 size={18} className="text-emerald-600" />
              <h3 className="text-base font-semibold text-slate-800">Evolução Mensal</h3>
            </div>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Sem série mensal para o período.</p>
            ) : (
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }} interval={isMobile ? 1 : 0} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={kFormatter} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={isMobile ? { fontSize: 10 } : undefined} />
                    <Bar dataKey="Receita Líquida" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="EBITDA" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Resultado Líquido" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modais de memória de cálculo */}
      {breakdownTarget && (
        <DreBreakdownModal
          basis={basis}
          year={year}
          month={monthOrNull}
          companyId={companyId}
          companyLabel={companyLabel}
          dreGroupId={breakdownTarget.dreGroupId}
          groupCode={breakdownTarget.code}
          groupName={breakdownTarget.name}
          onClose={() => setBreakdownTarget(null)}
        />
      )}
      {formulaTarget && (
        <DreFormulaModal
          formula={formulaTarget}
          period={periodLabel(year, monthOrNull)}
          companyLabel={companyLabel}
          onClose={() => setFormulaTarget(null)}
        />
      )}
        </>
      )}
    </div>
  );
};

export default DreReportView;
